const SELECTORS = {
  slide: "[data-pms-slide]",
  video: "[data-pms-video]",
  videoPoster: "[data-pms-video-poster]",
  playOverlay: "[data-pms-play-overlay]",
  videoToggle: "[data-pms-video-toggle]",
  toggleIconPause: "[data-pms-video-toggle-icon-pause]",
  toggleIconPlay: "[data-pms-video-toggle-icon-play]",
};

class ProductMediaSlider {
  #scrollContainer = null;
  #prevButton = null;
  #nextButton = null;

  #isDragging = false;
  #startX = 0;
  #initialScrollLeft = 0;
  #hasDragged = false;

  #boundOnMouseDown = null;
  #boundOnMouseMove = null;
  #boundOnMouseUp = null;
  #boundOnMouseLeave = null;
  #boundOnClick = null;
  #boundOnScroll = null;

  #lazyImageObserver = null;
  #videoSlideObserver = null;

  constructor(scrollContainer, prevButton, nextButton) {
    this.#scrollContainer = scrollContainer;
    this.#prevButton = prevButton ?? null;
    this.#nextButton = nextButton ?? null;

    this.#boundOnMouseDown = this.#onMouseDown.bind(this);
    this.#boundOnMouseMove = this.#onMouseMove.bind(this);
    this.#boundOnMouseUp = this.#onMouseUp.bind(this);
    this.#boundOnMouseLeave = this.#onMouseLeave.bind(this);
    this.#boundOnClick = this.#onClick.bind(this);

    this.#scrollContainer.addEventListener("mousedown", this.#boundOnMouseDown);
    this.#scrollContainer.addEventListener("mousemove", this.#boundOnMouseMove);
    this.#scrollContainer.addEventListener("mouseup", this.#boundOnMouseUp);
    this.#scrollContainer.addEventListener("mouseleave", this.#boundOnMouseLeave);
    this.#scrollContainer.addEventListener("click", this.#boundOnClick, true);

    this.#setupLazyImages();
    this.#setupVideoControls();
    this.#setupVideoSlideTracking();
  }

  #setupLazyImages() {
    const lazyImages = this.#scrollContainer.querySelectorAll("img[data-lazy-src]");
    if (lazyImages.length === 0) return;

    if (typeof IntersectionObserver === "undefined") {
      lazyImages.forEach((img) => this.#hydrateImage(img));
      return;
    }

    this.#lazyImageObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.#hydrateImage(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        root: this.#scrollContainer,
        rootMargin: "0px 200% 0px 200%",
        threshold: 0,
      },
    );

    lazyImages.forEach((img) => this.#lazyImageObserver.observe(img));
  }

  #hydrateImage(img) {
    const src = img.dataset.lazySrc;
    const srcset = img.dataset.lazySrcset;
    const sizes = img.dataset.lazySizes;

    if (srcset) img.srcset = srcset;
    if (sizes) img.sizes = sizes;
    if (src) img.src = src;

    delete img.dataset.lazySrc;
    delete img.dataset.lazySrcset;
    delete img.dataset.lazySizes;
  }

  #setupNavigation() {
    this.#prevButton?.addEventListener("click", () => {
      this.#scrollContainer.scrollBy({
        left: -this.#scrollContainer.offsetWidth,
        behavior: "smooth",
      });
    });

    this.#nextButton?.addEventListener("click", () => {
      this.#scrollContainer.scrollBy({
        left: this.#scrollContainer.offsetWidth,
        behavior: "smooth",
      });
    });
  }


  #onMouseDown(event) {
    this.#isDragging = true;
    this.#hasDragged = false;
    this.#startX = event.pageX - this.#scrollContainer.offsetLeft;
    this.#initialScrollLeft = this.#scrollContainer.scrollLeft;
    this.#scrollContainer.style.scrollSnapType = "none";
    this.#scrollContainer.style.cursor = "grabbing";
    this.#scrollContainer.style.userSelect = "none";
  }

  #onMouseMove(event) {
    if (!this.#isDragging) return;

    const currentX = event.pageX - this.#scrollContainer.offsetLeft;
    const delta = currentX - this.#startX;

    if (Math.abs(delta) > 5) {
      this.#hasDragged = true;
    }

    this.#scrollContainer.scrollLeft = this.#initialScrollLeft - delta;
  }

  #onMouseUp() {
    this.#endDrag();
  }

  #onMouseLeave() {
    this.#endDrag();
  }

  #endDrag() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#scrollContainer.style.cursor = "";
    this.#scrollContainer.style.userSelect = "";
    this.#snapToNearestImage();
  }

  #snapToNearestImage() {
    const imageWidth = this.#scrollContainer.offsetWidth;
    const nearestIndex = Math.round(this.#scrollContainer.scrollLeft / imageWidth);
    const targetScrollLeft = nearestIndex * imageWidth;

    this.#scrollContainer.scrollTo({ left: targetScrollLeft, behavior: "smooth" });

    const reenableSnap = () => {
      if (!this.#isDragging) {
        this.#scrollContainer.style.scrollSnapType = "";
      }
    };

    if ("onscrollend" in window) {
      this.#scrollContainer.addEventListener("scrollend", reenableSnap, { once: true });
    } else {
      setTimeout(reenableSnap, 400);
    }
  }

  #onClick(event) {
    if (this.#hasDragged) {
      event.stopPropagation();
      event.preventDefault();
      this.#hasDragged = false;
    }
  }

  #setupVideoControls() {
    this.#scrollContainer.querySelectorAll(SELECTORS.playOverlay).forEach((overlay) => {
      overlay.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          this.#startVideo(overlay);
        } catch (error) {
          console.error("[ProductMediaSlider:startVideo]", error);
        }
      });
    });

    this.#scrollContainer.querySelectorAll(SELECTORS.videoToggle).forEach((toggle) => {
      toggle.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          this.#toggleVideo(toggle);
        } catch (error) {
          console.error("[ProductMediaSlider:toggleVideo]", error);
        }
      });
    });

    this.#scrollContainer.querySelectorAll(SELECTORS.video).forEach((video) => {
      video.addEventListener("play", () => this.#syncToggleIcon(video.closest(SELECTORS.slide), "playing"));
      video.addEventListener("pause", () => this.#syncToggleIcon(video.closest(SELECTORS.slide), "paused"));
    });
  }

  #startVideo(overlay) {
    const slide = overlay.closest(SELECTORS.slide);
    if (!slide) return;
    const mediaType = slide.dataset.pmsMediaType;

    this.#pauseAllVideosExcept(slide);

    if (mediaType === "video") {
      const video = slide.querySelector(SELECTORS.video);
      if (!video) return;
      video.classList.remove("hidden");
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => console.error("[ProductMediaSlider:videoPlay]", error));
      }
      overlay.setAttribute("hidden", "");
      slide.querySelector(SELECTORS.videoToggle)?.removeAttribute("hidden");
    } else if (mediaType === "external_video") {
      const embedUrl = slide.dataset.pmsEmbedUrl;
      const host = slide.dataset.pmsHost;
      if (!embedUrl) return;
      const iframe = document.createElement("iframe");
      iframe.src = this.#buildExternalAutoplayUrl(embedUrl, host);
      iframe.className = "absolute inset-0 w-full h-full z-0";
      iframe.setAttribute("allow", "autoplay; fullscreen; encrypted-media; picture-in-picture");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");
      slide.appendChild(iframe);
      slide.querySelector(SELECTORS.videoPoster)?.classList.add("hidden");
      overlay.setAttribute("hidden", "");
    }
  }

  #buildExternalAutoplayUrl(embedUrl, host) {
    const sep = embedUrl.includes("?") ? "&" : "?";
    if (host === "youtube") {
      return `${embedUrl}${sep}autoplay=1&mute=1&playsinline=1&controls=0&rel=0`;
    }
    if (host === "vimeo") {
      return `${embedUrl}${sep}autoplay=1&muted=1&loop=1&playsinline=1`;
    }
    return `${embedUrl}${sep}autoplay=1`;
  }

  #toggleVideo(toggle) {
    const slide = toggle.closest(SELECTORS.slide);
    const video = slide?.querySelector(SELECTORS.video);
    if (!video) return;
    if (video.paused) {
      this.#pauseAllVideosExcept(slide);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }

  #syncToggleIcon(slide, state) {
    if (!slide) return;
    const pauseIcon = slide.querySelector(SELECTORS.toggleIconPause);
    const playIcon = slide.querySelector(SELECTORS.toggleIconPlay);
    const toggle = slide.querySelector(SELECTORS.videoToggle);
    if (state === "playing") {
      pauseIcon?.classList.remove("hidden");
      playIcon?.classList.add("hidden");
      toggle?.setAttribute("aria-label", toggle.dataset.labelPause ?? "");
    } else {
      pauseIcon?.classList.add("hidden");
      playIcon?.classList.remove("hidden");
      toggle?.setAttribute("aria-label", toggle.dataset.labelPlay ?? "");
    }
  }

  #pauseAllVideosExcept(currentSlide) {
    this.#scrollContainer.querySelectorAll(SELECTORS.video).forEach((video) => {
      const slide = video.closest(SELECTORS.slide);
      if (slide !== currentSlide && !video.paused) {
        video.pause();
      }
    });
    this.#scrollContainer.querySelectorAll(SELECTORS.slide).forEach((slide) => {
      if (slide === currentSlide) return;
      if (slide.dataset.pmsMediaType === "external_video") {
        this.#resetExternalSlide(slide);
      }
    });
  }

  #resetExternalSlide(slide) {
    const iframe = slide.querySelector("iframe");
    if (iframe) iframe.remove();
    slide.querySelector(SELECTORS.videoPoster)?.classList.remove("hidden");
    slide.querySelector(SELECTORS.playOverlay)?.removeAttribute("hidden");
  }

  #setupVideoSlideTracking() {
    const slides = this.#scrollContainer.querySelectorAll(SELECTORS.slide);
    if (!slides.length) return;
    if (typeof IntersectionObserver === "undefined") return;

    this.#videoSlideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0.6) return;
          const slide = entry.target;
          const video = slide.querySelector(SELECTORS.video);
          if (video && !video.paused) video.pause();
          if (slide.dataset.pmsMediaType === "external_video") {
            this.#resetExternalSlide(slide);
          }
        });
      },
      {
        root: this.#scrollContainer,
        threshold: [0, 0.6],
      },
    );
    slides.forEach((slide) => this.#videoSlideObserver.observe(slide));
  }

  destroy() {
    this.#scrollContainer.removeEventListener("mousedown", this.#boundOnMouseDown);
    this.#scrollContainer.removeEventListener("mousemove", this.#boundOnMouseMove);
    this.#scrollContainer.removeEventListener("mouseup", this.#boundOnMouseUp);
    this.#scrollContainer.removeEventListener("mouseleave", this.#boundOnMouseLeave);
    this.#scrollContainer.removeEventListener("click", this.#boundOnClick, true);
    this.#scrollContainer.removeEventListener("scroll", this.#boundOnScroll);
    this.#lazyImageObserver?.disconnect();
    this.#lazyImageObserver = null;
    this.#videoSlideObserver?.disconnect();
    this.#videoSlideObserver = null;
  }
}

document.querySelectorAll("[data-drag-slider]").forEach((scrollContainer) => {
  const sliderId = scrollContainer.id;
  const prevButton = document.querySelector(`[data-slider-prev="${sliderId}"]`);
  const nextButton = document.querySelector(`[data-slider-next="${sliderId}"]`);
  new ProductMediaSlider(scrollContainer, prevButton, nextButton);
});
